'use client'

import { Divider } from '@lastbrand/web-ui/divider'
import { Skeleton } from '@lastbrand/web-ui/skeleton'
import { css } from '@lastbrand/web-ui-styled-system/css'

import InfiniteScrollProductListDS from '@src/components/blocks/infinite-scroll-product-list-ds'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { SectionContentContainer } from '@src/ds/blocks/section-content-container'
import { trackingMetaData } from '@src/pages/[...slug]/components/collection/collection.constants'
import CollectionFilters from '@src/pages/[...slug]/components/collection/molecules/collectionFilters/CollectionFilters'
import NoProduct from '@src/pages/[...slug]/components/collection/molecules/noProducts'
import { LoaderWrapper } from '@src/quince-components/src'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'

import { MINI_PDP_COLLECTION_SECTION_NAME_FOR_REF } from '../../organisms/productPageSections/miniPDPCollection/constant'
import { useMiniPDPCollection } from '../../organisms/productPageSections/miniPDPCollection/useMiniPDPCollection'
import { MiniPDPProductImagesDS } from '../../organisms/productPageSections/miniPDPProductImages/mini-pdp-product-images-ds'
import { MiniPDPProductPurchaseDS } from '../../organisms/productPageSections/miniPDPProductPurchase/mini-pdp-product-purchase-ds'

import type { ProductPageContext } from '../../domains/MiniPDPContext'

const TRACKING_CATEGORY_NAVIGATION = 'navigation'
const SKELETON_CARD_COUNT = 8

const topRowClass = css({ display: 'flex', flexDirection: { base: 'column', lg: 'row' } })

const productGridClass = css({
  display: 'grid',
  w: 'full',
  gridTemplateColumns: {
    base: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(4, minmax(0, 1fr))',
  },
  justifyItems: 'center',
  columnGap: 'space-16',
  rowGap: { base: 'space-32' },
  '& > *': {
    w: 'full',
  },
})

const boundaryClass = css({ height: '1px', visibility: 'hidden' })

const INFINITE_SCROLL_LOADER_MIN_HEIGHT = '400px'

// STRFRNT-16531: the divider under the product details rendered full-bleed because it was a
// SIBLING of the SectionContentContainer wrapping every other block here, so it never picked up
// the DS content frame. Wrapping it in the same container aligns its ends with the buy box above
// and the filters/grid below (1504px inner frame + space-16/space-48 gutters = the 1600px band
// the design spec asks for). Vertical spacing rides on the outer band per the container's contract.
const dividerBandClass = css({ marginTop: { base: 'space-24', lg: 'space-32' } })

// Matches the filters bar's own `marginBottom: space-24`, so the chips sit centred between the
// divider and the grid (Figma Widget-Filters: 24 / 32 / 24).
const filterSectionClass = css({ paddingTop: { base: 'space-8', lg: 'space-24' } })

const productSectionClass = css({ paddingBottom: { base: 'space-80', lg: 'space-100' } })

function ProductCardSkeleton() {
  return (
    <div>
      <Skeleton css={{ width: 'full', aspectRatio: '4 / 5' }} />
      <Skeleton css={{ width: '70%', height: '16px', marginTop: 'space-12' }} />
      <Skeleton css={{ width: '40%', height: '16px', marginTop: 'space-8' }} />
    </div>
  )
}

export const MiniPDPDS = ({ pageData }: { pageData: ProductPageContext }) => {
  const {
    collectionPage,
    totalProductsCount,
    initialFilters,
    sortBy,
    selectedFilters,
    updateQuery,
    showSubCollectionsPageLoader,
    showNextFeedLoading,
    filteredSubCollection,
    totalProductsCurrentlyOnPage,
    intersectRef,
    productViewTracker,
    filterSectionRef,
    productSectionRef,
    productCardsSectionRef,
    tracker,
  } = useMiniPDPCollection({
    collectionId: pageData.product.productRecommendations.mpdp.data.id,
    product: pageData.product,
    sectionData: pageData.sectionData,
  })

  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP
  const eagerLoadCount = isDesktop ? 4 : 2

  return (
    <>
      <SectionContentContainer contentClassName={topRowClass}>
        <MiniPDPProductImagesDS sectionData={pageData.sectionData} product={pageData.product} />
        <MiniPDPProductPurchaseDS product={pageData.product} sectionData={pageData.sectionData} />
      </SectionContentContainer>

      <SectionContentContainer className={dividerBandClass}>
        <Divider />
      </SectionContentContainer>

      <div id="filter-section" ref={filterSectionRef} className={filterSectionClass}>
        <CollectionFilters
          totalProductsCount={totalProductsCount}
          initialFilters={initialFilters}
          sortBy={sortBy}
          selectedFilters={selectedFilters}
          updateQuery={updateQuery}
          collectionPage={collectionPage}
          showLoader={showSubCollectionsPageLoader && initialFilters.length === 0}
          subCollectionCards={[]}
        />
      </div>

      <div
        ref={productSectionRef}
        data-section-name={MINI_PDP_COLLECTION_SECTION_NAME_FOR_REF}
        data-testid={TEST_LOCATORS.MINI_PDP.COLLECTION_SECTION}
      >
        <SectionContentContainer className={productSectionClass}>
          {showSubCollectionsPageLoader ? (
            <div className={productGridClass}>
              {Array.from({ length: isDesktop ? SKELETON_CARD_COUNT : SKELETON_CARD_COUNT / 2 }).map((_, index) => (
                <ProductCardSkeleton key={index} />
              ))}
            </div>
          ) : (
            <>
              <InfiniteScrollProductListDS
                productCards={filteredSubCollection?.productCards ?? []}
                productTestIdPrefix={TEST_LOCATORS.MINI_PDP.COLLECTION_PRODUCT_PREFIX}
                swiperClassName={productGridClass}
                optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                tracker={tracker}
                productViewTracker={productViewTracker}
                productClickTrackConfig={{
                  trackInternal: true,
                  trackGA: true,
                  category: TRACKING_CATEGORY_NAVIGATION,
                  action: 'click_subcollection_product',
                  context: { ...trackingMetaData.context },
                }}
                showWhiteGloveDeliveryTag
                eagerLoadImageCount={eagerLoadCount}
              />
              <div id="boundary_bottom" className={boundaryClass} ref={intersectRef} />
              <LoaderWrapper
                testid={TEST_LOCATORS.COLLECTION_INFINITE_LOADER}
                showLoader={showNextFeedLoading}
                minHeight={INFINITE_SCROLL_LOADER_MIN_HEIGHT}
              >
                <div />
              </LoaderWrapper>
            </>
          )}
        </SectionContentContainer>
      </div>
      {totalProductsCurrentlyOnPage === 0 && !showSubCollectionsPageLoader && (
        <NoProduct
          onResetFilters={() => {
            updateQuery({
              filter: { type: 'reset' },
              sort: { type: 'reset' },
            })
          }}
          ref={productCardsSectionRef}
        />
      )}
    </>
  )
}
